import axios from "../../api/axios";
import { getAccessToken } from "../../api/session";

let isInterceptorSetup = false;

/**
 * Setup axios interceptors for automatic token refresh
 */
function setupInterceptors() {
  if (isInterceptorSetup) {
    return; // Prevent duplicate interceptors
  }

  // Request interceptor - add token to all requests
  axios.interceptors.request.use(
    async (config) => {
      // Skip token for token refresh endpoint to avoid infinite loop
      if (config.url?.includes("/oauth/token")) {
        return config;
      }

      try {
        // Get token (will refresh if expired)
        const token = await getAccessToken();
        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
        } else {
          console.warn("No token available for request:", config.url);
          // Don't send request without token - let it fail so response interceptor can handle it
        }
      } catch (error) {
        console.error("Failed to get token in request interceptor:", error);
        // If we can't get token, the request will fail
        // Response interceptor will try to refresh and retry
      }

      return config;
    },
    (error) => {
      console.error("Request interceptor error:", error);
      return Promise.reject(error);
    }
  );

  // Response interceptor - handle token expiration
  axios.interceptors.response.use(
    (response) => {
      // Success - just return the response
      return response;
    },
    async (error) => {
      // Handle network errors or request cancellation
      if (!error.response && !error.config) {
        console.error("Network error or request cancelled:", error);
        return Promise.reject(error);
      }

      const originalRequest = error.config;

      // Skip retry for token endpoint
      if (originalRequest?.url?.includes("/oauth/token")) {
        return Promise.reject(error);
      }

      // If error is 401/403 and we haven't retried yet
      if (
        error.response &&
        (error.response.status === 401 || error.response.status === 403) &&
        originalRequest &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;

        try {
          // Get new token
          const newToken = await getAccessToken(true);
          
          if (!newToken) {
            throw new Error("Failed to get new token");
          }

          // Update the authorization header
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;

          // Retry the original request with new token
          return axios(originalRequest);
        } catch (refreshError) {
          // If token refresh fails, reject the original error
          console.error("Token refresh failed in response interceptor:", refreshError);
          return Promise.reject(error);
        }
      }

      // For all other errors (including network errors), reject normally
      return Promise.reject(error);
    }
  );

  isInterceptorSetup = true;
}

/**
 * Initialize interceptor with initial token
 */
export default function initializeInterceptor(accessToken) {
  setupInterceptors();
}
